import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, TrendingDown } from "lucide-react";

const StatsCard = ({
  title,
  value,
  change,
  icon: Icon,
  index,
  description,
}) => {
  const reduceMotion = useReducedMotion();
  const isPositive = change > 0;

  const colorSchemes = [
    {
      bg: "from-indigo-50 via-indigo-50 to-violet-100",
      icon: "from-indigo-600 to-violet-600",
      text: "text-indigo-700",
      rail: "from-indigo-500 to-violet-500",
    },
    {
      bg: "from-violet-50 via-violet-50 to-fuchsia-100",
      icon: "from-violet-600 to-fuchsia-600",
      text: "text-violet-700",
      rail: "from-violet-500 to-fuchsia-500",
    },
    {
      bg: "from-sky-50 via-sky-50 to-indigo-100",
      icon: "from-sky-600 to-indigo-600",
      text: "text-sky-700",
      rail: "from-sky-500 to-indigo-500",
    },
    {
      bg: "from-emerald-50 via-emerald-50 to-cyan-100",
      icon: "from-emerald-600 to-cyan-600",
      text: "text-emerald-700",
      rail: "from-emerald-500 to-cyan-500",
    },
    {
      bg: "from-amber-50 via-orange-50 to-rose-100",
      icon: "from-amber-600 to-orange-600",
      text: "text-amber-700",
      rail: "from-amber-500 to-orange-500",
    },
    {
      bg: "from-rose-50 via-fuchsia-50 to-violet-100",
      icon: "from-rose-600 to-violet-600",
      text: "text-indigo-700",
      rail: "from-rose-500 to-violet-500",
    },
  ];

  const colors = colorSchemes[index % colorSchemes.length];

  const formatValue = (val) => {
    if (typeof val === "number") {
      if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
      if (val >= 1000) return `${(val / 1000).toFixed(1)}K`;
      return val.toLocaleString();
    }
    return val;
  };

  const numericValue = Number.parseFloat(String(value).replace(/[^0-9.]/g, ""));
  const progressWidth = Number.isFinite(numericValue)
    ? Math.min(100, Math.max(14, numericValue > 100 ? numericValue % 100 : numericValue))
    : 65;

  return (
    <motion.div
      initial={{ scale: 0.96, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: index * 0.1 }}
      whileHover={reduceMotion ? undefined : { y: -3 }}
      className={`relative overflow-hidden rounded-xl border border-indigo-200/70 bg-gradient-to-br ${colors.bg} p-3 transition-all duration-200`}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-70" />

      <div className="flex items-start justify-between mb-2">
        <div className={`rounded-md bg-gradient-to-br ${colors.icon} p-1.5 shadow-[0_10px_24px_-18px_rgba(15,23,42,0.9)]`}>
          <Icon className="w-3.5 h-3.5 text-white" />
        </div>

        <div
          className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
            isPositive
              ? "bg-emerald-100 text-emerald-700"
              : "bg-rose-100 text-rose-700"
          }`}
        >
          {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {isPositive ? "+" : ""}
          {change}%
        </div>
      </div>

      <div>
        <p className="mb-0.5 text-xs font-medium text-slate-600">
          {title}
        </p>
        <p className="text-xl font-bold text-slate-900">
          {formatValue(value)}
        </p>
        {description && (
          <p className="mt-1 text-[11px] text-slate-500">
            {description}
          </p>
        )}
      </div>

      <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/60">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progressWidth}%` }}
          transition={{ duration: 1, delay: index * 0.1 + 0.3 }}
          className={`h-full rounded-full bg-gradient-to-r ${colors.rail}`}
        />
      </div>
    </motion.div>
  );
};

export default StatsCard;
